/*
  Account drawer + browser-side product memory.

  The reference storefront runs Klaviyo's Customer Hub for this panel. Nothing
  here talks to an app: "recently viewed" and "favourites" are lists of product
  handles kept in localStorage and hydrated from /products/<handle>.js on open.

  That has one honest limit worth knowing: both lists live in the browser, so
  they do not follow a shopper to another device. Real cross-device history
  needs either an app or customer metafields written from a backend.
*/

const BrandStore = {
  keys: { viewed: 'brand:recently-viewed', favorites: 'brand:favorites' },
  limit: 12,

  read(key) {
    try {
      const raw = localStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      // Private mode, a full quota, or hand-edited junk. An empty list is the
      // right answer for all three — the panel just shows its empty state.
      return [];
    }
  },

  write(key, list) {
    try {
      localStorage.setItem(key, JSON.stringify(list.slice(0, this.limit)));
    } catch (e) {
      /* storage unavailable: the feature degrades, the page does not break */
    }
  },

  remember(handle) {
    if (!handle) return;
    const list = this.read(this.keys.viewed).filter((h) => h !== handle);
    list.unshift(handle);
    this.write(this.keys.viewed, list);
  },

  favorites() {
    return this.read(this.keys.favorites);
  },

  isFavorite(handle) {
    return this.favorites().includes(handle);
  },

  toggleFavorite(handle) {
    const list = this.favorites();
    const at = list.indexOf(handle);
    if (at === -1) {
      list.unshift(handle);
    } else {
      list.splice(at, 1);
    }
    this.write(this.keys.favorites, list);
    document.dispatchEvent(new CustomEvent('brand:favorites:change', { detail: { handle } }));
    return at === -1;
  },
};

/* ------------------------------------------------------------------ *
   Heart button on product cards
 * ------------------------------------------------------------------ */

class BrandFavoriteButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    if (!this.button || !this.handle) return;

    this.sync();
    this.button.addEventListener('click', this.onClick.bind(this));
    this.onExternalChange = this.sync.bind(this);
    document.addEventListener('brand:favorites:change', this.onExternalChange);
  }

  disconnectedCallback() {
    if (this.onExternalChange) document.removeEventListener('brand:favorites:change', this.onExternalChange);
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    BrandStore.toggleFavorite(this.handle);
  }

  sync() {
    const on = BrandStore.isFavorite(this.handle);
    this.classList.toggle('is-active', on);
    this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = on ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.button.setAttribute('aria-label', label);
  }
}

customElements.define('brand-favorite-button', BrandFavoriteButton);

/* ------------------------------------------------------------------ *
   The drawer
 * ------------------------------------------------------------------ */

class BrandAccountDrawer extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('.brand-account__inner');
    this.overlay = this.querySelector('.brand-account__overlay');
    this.activeElement = null;
    this.hydrated = false;

    // Product pages log themselves as soon as the drawer element is parsed.
    if (this.dataset.currentProduct) BrandStore.remember(this.dataset.currentProduct);

    this.querySelectorAll('[data-brand-account-close]').forEach((el) =>
      el.addEventListener('click', this.close.bind(this))
    );
    if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    document.addEventListener('brand:favorites:change', () => {
      if (this.classList.contains('active')) this.renderList('favorites');
    });

    this.bindToggle();
  }

  bindToggle() {
    // Two triggers: the header icon on desktop, the menu drawer link on
    // phones, where the header has no room for a third icon.
    document.querySelectorAll('[data-brand-account-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-haspopup', 'dialog');
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeMenuDrawer();
        this.open(toggle);
      });
    });
  }

  closeMenuDrawer() {
    document.querySelectorAll('header-drawer details[open]').forEach((d) => d.removeAttribute('open'));
    document.querySelector('header-drawer')?.classList.remove('menu-opening');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.activeElement = triggeredBy || document.activeElement;
    setTimeout(() => this.classList.add('animate', 'active'));
    document.body.classList.add('overflow-hidden');

    if (!this.hydrated) {
      this.hydrated = true;
      this.renderList('viewed');
      this.renderList('favorites');
    } else {
      this.renderList('favorites');
    }

    this.addEventListener(
      'transitionend',
      () => {
        if (typeof trapFocus === 'function') trapFocus(this.inner, this.querySelector('.brand-account__close'));
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  /* -- lists -- */

  async renderList(kind) {
    const region = this.querySelector(`[data-brand-list="${kind}"]`);
    if (!region) return;

    const handles =
      kind === 'favorites'
        ? BrandStore.favorites()
        : BrandStore.read(BrandStore.keys.viewed).filter((h) => h !== this.dataset.currentProduct);

    const empty = region.querySelector('[data-brand-list-empty]');
    const track = region.querySelector('[data-brand-list-track]');
    if (!track) return;

    if (handles.length === 0) {
      track.innerHTML = '';
      region.hidden = kind === 'viewed';
      if (empty) empty.hidden = false;
      return;
    }

    region.hidden = false;
    if (empty) empty.hidden = true;

    const products = (await Promise.all(handles.slice(0, 8).map((h) => this.fetchProduct(h)))).filter(Boolean);
    if (products.length === 0) {
      track.innerHTML = '';
      region.hidden = kind === 'viewed';
      if (empty) empty.hidden = false;
      return;
    }

    track.innerHTML = products.map((p) => this.cardMarkup(p)).join('');
  }

  async fetchProduct(handle) {
    this.cache = this.cache || {};
    if (handle in this.cache) return this.cache[handle];
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) throw new Error(response.status);
      const product = await response.json();
      this.cache[handle] = product;
      return product;
    } catch (e) {
      // Product unpublished or deleted since it was stored. Cache the miss so a
      // dead handle is not refetched every time the drawer opens.
      this.cache[handle] = null;
      return null;
    }
  }

  cardMarkup(product) {
    const image = product.featured_image
      // Square width/height so the attributes agree with the CSS aspect-ratio;
      // mismatched ones are presentational hints that win and squash the box.
      ? `<img class="brand-account-card__image" src="${this.thumb(product.featured_image)}" alt="" loading="lazy" width="240" height="240">`
      : '<span class="brand-account-card__image brand-account-card__image--empty"></span>';

    const price = this.formatMoney(product.price);

    return `<li class="brand-account-card">
      <a class="brand-account-card__link" href="${window.Shopify.routes.root}products/${product.handle}">
        ${image}
        <span class="brand-account-card__title">${this.escape(product.title)}</span>
        <span class="brand-account-card__price">${price}</span>
      </a>
    </li>`;
  }

  thumb(url) {
    // Shopify CDN sizing goes before the extension, e.g. foo_240x.jpg
    return String(url).replace(/(\.[a-z]+)(\?.*)?$/i, '_240x$1$2');
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    // Only the two plain formats are worth handling here; anything else falls
    // back to the store's own currency string via Intl.
    if (format.includes('{{amount}}')) return format.replace('{{amount}}', this.withSeparators(amount, '.', ','));
    if (format.includes('{{amount_with_comma_separator}}'))
      return format.replace('{{amount_with_comma_separator}}', this.withSeparators(amount, ',', '.'));
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  withSeparators(amount, decimal, thousands) {
    const [whole, fraction] = amount.split('.');
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${decimal}${fraction}`;
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('brand-account-drawer', BrandAccountDrawer);
